import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Trash2, Plus, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useTaal } from "@/contexts/TaalContext";

/**
 * The desktop side: which phones may reach this machine, and taking that back.
 *
 * The confirmation step is not decoration. Anyone who photographs the QR can
 * get as far as a number appearing on their own screen; it is a different
 * number, and comparing the two is what keeps them out. So the wording asks
 * the owner to compare rather than to click yes.
 */
export default function PairedDevices() {
  const { t } = useTaal();
  const [devices, setDevices] = useState([]);
  const [pairing, setPairing] = useState(null); // {offer, url, expiresAt}
  const [state, setState] = useState("idle"); // idle | waiting | claimed
  const [code, setCode] = useState(null);
  const [deviceName, setDeviceName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reachableVia, setReachableVia] = useState(null);
  const [unreachable, setUnreachable] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState(null);
  const polling = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/devices`);
      setDevices(data.devices || []);
    } catch {
      // A list that cannot be fetched is not worth a toast on every open.
    }
  }, []);

  useEffect(() => {
    load();
    // What a phone will be sent to. Detection cannot always work it out from
    // inside a packaged app, so this is the value the owner can set once.
    axios
      .get(`${API}/devices/public-url`)
      .then(({ data }) => {
        setSavedUrl(data.public_url);
        setPublicUrl(data.public_url || "");
      })
      .catch(() => {});
    return () => clearInterval(polling.current);
  }, [load]);

  const start = async () => {
    setBusy(true);
    setUnreachable(null);
    try {
      // Sent only so the backend can use it when it is a real address. Opened
      // on localhost, as the desktop app always is, it is worse than nothing:
      // on a phone localhost is the phone. The backend decides.
      const { data } = await axios.post(`${API}/devices/pairing`, {
        server_url: window.location.origin,
      });
      setReachableVia(data.reachable_via);
      // The secret rides in the fragment: never sent to a server, never in a
      // log, never in a Referer header.
      const url = `${data.qr.server}/pair#offer=${encodeURIComponent(data.offer)}&secret=${encodeURIComponent(data.qr.secret)}`;
      setPairing({ offer: data.offer, url });
      setState("waiting");
      setCode(null);

      polling.current = setInterval(async () => {
        try {
          const { data: status } = await axios.get(`${API}/devices/pairing/${data.offer}`);
          if (status.code) {
            setCode(status.code);
            setDeviceName(status.device_name);
            setState("claimed");
          }
        } catch {
          clearInterval(polling.current);
          setState("idle");
          setPairing(null);
        }
      }, 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "string") {
        // No address a phone could reach. Say why rather than show a code
        // that leads to an error page.
        setUnreachable(detail);
      } else {
        toast.error(t("Koppelen kon niet starten"));
      }
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    clearInterval(polling.current);
    if (pairing) {
      await axios.post(`${API}/devices/pairing/cancel`, { offer: pairing.offer, secret: "" }).catch(() => {});
    }
    setPairing(null);
    setState("idle");
    setCode(null);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/devices/pairing/${pairing.offer}/confirm`);
      clearInterval(polling.current);
      setPairing(null);
      setState("idle");
      setCode(null);
      await load();
      toast.success(t("Telefoon gekoppeld"));
    } catch {
      toast.error(t("Deze koppeling kon niet worden bevestigd"));
    } finally {
      setBusy(false);
    }
  };

  const savePublicUrl = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/devices/public-url`, {
        public_url: publicUrl.trim() || null,
      });
      setSavedUrl(data.public_url);
      setUnreachable(null);
      toast.success(data.public_url ? t("Adres opgeslagen") : t("Adres gewist"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("Dat adres kon niet worden opgeslagen"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (device) => {
    try {
      await axios.delete(`${API}/devices/${device.id}`);
      await load();
      toast.success(t("{d} kan deze machine niet meer bereiken", { d: device.name || t("Apparaat") }));
    } catch {
      toast.error(t("Dit apparaat kon niet worden ingetrokken"));
    }
  };

  return (
    <div className="space-y-4" data-testid="paired-devices">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{t("Gekoppelde telefoons")}</p>
          <p className="text-xs text-muted-foreground">
            {t("Een gekoppelde telefoon bereikt deze machine zonder wachtwoord, via een adres dat je al hebt, zoals Tailscale.")}
          </p>
        </div>
        {state === "idle" && (
          <Button size="sm" onClick={start} disabled={busy} data-testid="start-pairing-btn">
            <Plus className="h-4 w-4 mr-1" />
            {t("Telefoon koppelen")}
          </Button>
        )}
      </div>

      {unreachable && (
        <p className="text-xs text-corona border border-corona/30 rounded-lg p-3" data-testid="pairing-unreachable">
          {unreachable}
        </p>
      )}

      <div className="space-y-1">
        <label htmlFor="public-url" className="text-xs text-muted-foreground">
          {t("Adres waarop een telefoon deze machine bereikt")}
          {savedUrl ? "" : ` ${t("(waar het kan automatisch bepaald)")}`}
        </label>
        <div className="flex gap-2">
          <input
            id="public-url"
            value={publicUrl}
            onChange={(e) => setPublicUrl(e.target.value)}
            placeholder="https://your-machine.tailnet.ts.net:8443"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="flex-1 min-w-0 rounded-lg border bg-transparent px-3 py-2 text-sm"
            data-testid="public-url-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={savePublicUrl}
            disabled={busy || publicUrl.trim() === (savedUrl || "")}
            data-testid="save-public-url-btn"
          >
            {t("Opslaan")}
          </Button>
        </div>
      </div>

      {state !== "idle" && pairing && (
        <div className="border rounded-xl p-4 space-y-4 text-center" data-testid="pairing-panel">
          {state === "waiting" && (
            <>
              <p className="text-sm text-muted-foreground">
                {t("Scan dit met de camera van je telefoon.")}
              </p>
              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-lg" data-testid="pairing-qr">
                  <QRCodeSVG value={pairing.url} size={180} />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground break-all" data-testid="pairing-url">
                Your phone will open {pairing.url.split("#")[0]}
                {reachableVia ? ` (found via ${reachableVia})` : ""}
              </p>
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("Wachten op een telefoon")}
              </p>
            </>
          )}

          {state === "claimed" && (
            <>
              <p className="text-sm">
                {deviceName || "Een telefoon"} wil koppelen. Toont hij dit getal?
              </p>
              <p className="text-4xl font-mono tracking-widest" data-testid="pairing-code">
                {code?.slice(0, 3)} {code?.slice(3)}
              </p>
              <p className="text-xs text-corona flex items-start gap-2 text-left">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                {t("Toont je telefoon een ander getal, dan heeft iemand anders de code gescand. Niet bevestigen.")}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={stop}>
                  {t("Ze zijn anders")}
                </Button>
                <Button size="sm" onClick={confirm} disabled={busy} data-testid="confirm-pairing-btn">
                  {t("Ja, dat is het getal")}
                </Button>
              </div>
            </>
          )}

          {state === "waiting" && (
            <Button variant="ghost" size="sm" onClick={stop}>
              {t("Annuleren")}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-2">
        {devices.length === 0 && (
          <p className="text-xs text-muted-foreground">{t("Nog geen telefoons gekoppeld.")}</p>
        )}
        {devices.map((device) => (
          <div
            key={device.id}
            className="flex items-center gap-3 border rounded-lg px-3 py-2"
            data-testid={`device-${device.id}`}
          >
            <Smartphone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm truncate">
                {device.name}
                {device.revoked && (
                  <span className="ml-2 text-[10px] uppercase text-corona">{t("ingetrokken")}</span>
                )}
              </p>
              <p className="text-[10px] text-muted-foreground font-mono truncate">
                {device.fingerprint}
              </p>
            </div>
            {!device.revoked && (
              <Button
                variant="ghost"
                size="icon"
                className="tap-target h-8 w-8 text-destructive"
                onClick={() => revoke(device)}
                aria-label={`${device.name} intrekken`}
                data-testid={`revoke-${device.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
